// Billet CNC production-planning estimator.
//
// Ported from the "BIW Billet Production Planning Hours" workbook, with one deliberate correction:
// the workbook derated cutting time TWICE for overlapping losses — once by a machine-efficiency
// factor (retracts, rapids, variable engagement) and again by an allowance percentage (air cutting,
// tool changes, repositioning). Those overlap, so the workbook over-estimated. Here each stage uses a
// SINGLE "effective process factor" (0-1) that captures in-cut losses once. Fixed on-machine setup and
// the schedule contingency remain as separate, non-overlapping layers.
//
// Roughing supports one OR MORE stages (e.g. a bulk high-feed pass plus a slower rest-rough), each with
// its own share of the removal volume, rate and factor. Everything here is pure and framework-free so it
// can be reused (a Production Tool tab, a quote line, a report) without pulling in the page. Call
// computeBilletPlan(inputs) for the full result. (Keys are alphabetical to satisfy the repo lint rules.)

import { MaterialType } from "@enums/materialType.enum";
import { densityForMaterialType } from "@helpers/materialTypeDensity";

export interface BilletPlanInputs {
	contingencyPct: number;       // 0-1 schedule contingency applied to base CNC hours
	densityKgM3: number;          // e.g. 7850 for steel
	finishAreaM2: number;         // surface area being finish-machined (from CAD)
	finishFeedMmMin: number;      // nominal finish feedrate
	finishPasses: number;         // equivalent surface-finishing passes
	finishedMassKg: number;       // CAD mass of the machined tool body
	finishingFactor: number;      // 0-1 effective process factor (replaces efficiency x allowance)
	finishingFixedHrs: number;    // fixed on-machine setup / prove-out for finishing
	heightMm: number;             // rectangular blank height
	hoursPerShift: number;        // productive machine hours in one shift
	lengthMm: number;             // rectangular blank length
	manualMassOverrideKg: number; // > 0 overrides rectangular mass (non-rectangular / pre-profiled stock)
	otherCncHrs: number;          // machine-occupied time not captured by roughing/finishing
	roughingStages: BilletRoughingStageInput[]; // one or more roughing passes
	shiftsPerDay: number;         // shifts per working day
	stepoverMm: number;           // effective finish stepover
	widthMm: number;              // rectangular blank width
}

export interface BilletPlanResult {
	baseCncHrs: number;
	breakdown: BilletPlanStageBreakdown[];
	buyToFlyRatio: number;
	contingencyHrs: number;
	effectiveFinishFeedMmMin: number;   // nominal x factor — the honest feed
	equivalentShifts: number;
	equivalentWorkingDays: number;
	finishToolpathLengthM: number;
	finishingCutHrs: number;
	finishingTotalHrs: number;
	massRemovedKg: number;
	materialRemovalPct: number;         // 0-1
	plannedOccupancyHrs: number;
	rectangularMassKg: number;
	removalVolumeCm3: number;
	roughingStages: BilletRoughingStageResult[]; // per-stage computed figures
	roughingTotalHrs: number;           // sum across all roughing stages
	startingMassKg: number;
}

export interface BilletPlanStageBreakdown {
	hours: number;
	pctOfPlanned: number; // 0-1
	stage: string;
}

export interface BilletRoughingStageInput {
	factor: number;     // 0-1 effective process factor (replaces efficiency x allowance)
	fixedHrs: number;   // fixed on-machine setup / prove-out for this stage
	mrrCm3Min: number;  // nominal material-removal rate for this stage
	share: number;      // 0-1 share of the total removal volume assigned to this stage
}

export interface BilletRoughingStageResult {
	cutHrs: number;
	effectiveMrrCm3Min: number; // nominal x factor
	fixedHrs: number;
	removalVolumeCm3: number;
	share: number;
	totalHrs: number;           // cut + fixed
}

export interface MrrLibraryEntry {
	finishFeedStart?: number;
	material: string;
	notes: string;
	roughMrrHigh?: number;
	roughMrrLow?: number;
	roughMrrStart?: number;
	strategy: string;
	useCase: string;
}

// Starting placeholders from the workbook's MRR Library. These are meant to be replaced with values
// back-calculated from real completed jobs. Treat them as effective (post-factor) starting points.
export const MRR_LIBRARY: MrrLibraryEntry[] = [
	{ material: "P20 / mild tooling steel", notes: "Refine with actual machine power, rigidity and tool data.", roughMrrHigh: 80, roughMrrLow: 30, roughMrrStart: 55, strategy: "Indexable shell / high-feed", useCase: "Large open pocket roughing" },
	{ material: "P20 / mild tooling steel", notes: "Lower effective MRR from retracts and variable engagement.", roughMrrHigh: 50, roughMrrLow: 15, roughMrrStart: 30, strategy: "Ball / bull-nose rest rough", useCase: "Complex 3D roughing" },
	{ finishFeedStart: 1800, material: "Tooling steel billet", notes: "Effective average feed after acceleration and 3D form.", strategy: "Ball / bull-nose finish", useCase: "Steel die-face finishing" },
	{ finishFeedStart: 1200, material: "Tooling steel billet", notes: "For small stepovers and surface-finish-driven passes.", strategy: "Small stepover / light scallop", useCase: "Fine finishing" },
];

// Sample from the workbook (a large BIW form block), so the tool opens on a recognisable case.
export const DEFAULT_BILLET_PLAN_INPUTS: BilletPlanInputs = {
	contingencyPct: 0.15,
	densityKgM3: 7850,
	finishAreaM2: 0.3239645,
	finishFeedMmMin: 1000,
	finishPasses: 1,
	finishedMassKg: 462.96,
	finishingFactor: 0.95,
	finishingFixedHrs: 0,
	heightMm: 140,
	hoursPerShift: 8,
	lengthMm: 1020,
	manualMassOverrideKg: 0,
	otherCncHrs: 0,
	roughingStages: [{ factor: 1, fixedHrs: 0, mrrCm3Min: 45.6, share: 0.96 }, { factor: 0.4, fixedHrs: 0, mrrCm3Min: 1.95, share: 0.03 }],
	shiftsPerDay: 2,
	stepoverMm: 0.4,
	widthMm: 550,
};

function round1(value: number): number {
	return Math.round((value + Number.EPSILON) * 10) / 10;
}

function safeDivide(numerator: number, denominator: number): number {
	return denominator > 0 ? numerator / denominator : 0;
}

// Density in kg/m³ for a material type, from the shared material-density map. weightConstant is density
// in g/cm³ x 1e-6, so x 1e9 gives kg/m³ (e.g. Steel 0.00000785 -> 7850). Returns 0 for NotApplicable.
export function densityKgM3ForMaterialType(materialType: MaterialType): number {
	return Math.round(densityForMaterialType(materialType) * 1000000000);
}

// Roughing MRR (cm³/min) from a feedrate and the cut cross-section: axial depth (ap) x radial width (ae)
// x feed (vf), in mm, converting mm³/min to cm³/min. Lets a programmer enter CAM numbers instead of a
// bare MRR — a feedrate alone can't give a removal rate without the cut cross-section.
export function mrrFromFeedAndEngagement(feedMmMin: number, depthMm: number, widthMm: number): number {
	return (depthMm * widthMm * feedMmMin) / 1000;
}

// Rectangular billet mass in kg: density (kg/m³) x volume (mm³) / 1e9.
export function rectangularMassKg(densityKgM3: number, lengthMm: number, widthMm: number, heightMm: number): number {
	return (densityKgM3 * lengthMm * widthMm * heightMm) / 1000000000;
}

// Removed mass converted to cm³ for the roughing MRR calculation.
export function removalVolumeCm3(massRemovedKg: number, densityKgM3: number): number {
	return safeDivide(massRemovedKg, densityKgM3) * 1000000;
}

// Volume-based roughing cut hours (before fixed setup). Single effective factor, no separate allowance.
export function roughingCutHours(roughingVolumeCm3: number, mrrCm3Min: number, factor: number): number {
	return round1(safeDivide(roughingVolumeCm3, mrrCm3Min * factor * 60));
}

// Approximate finish toolpath length in metres from area, stepover and passes.
export function finishToolpathLengthM(finishAreaM2: number, stepoverMm: number, passes: number): number {
	return round1(safeDivide(finishAreaM2 * 1000000, stepoverMm) * passes / 1000);
}

// Finish cut hours (before fixed setup) from toolpath length and effective feed.
export function finishingCutHours(toolpathLengthM: number, feedMmMin: number, factor: number): number {
	return round1(safeDivide(toolpathLengthM * 1000, feedMmMin * factor * 60));
}

export function computeBilletPlan(inputs: BilletPlanInputs): BilletPlanResult {
	// Stock
	const rectMass = rectangularMassKg(inputs.densityKgM3, inputs.lengthMm, inputs.widthMm, inputs.heightMm);
	const startingMass = inputs.manualMassOverrideKg > 0 ? inputs.manualMassOverrideKg : rectMass;
	const massRemoved = Math.max(startingMass - inputs.finishedMassKg, 0);
	const removalVol = removalVolumeCm3(massRemoved, inputs.densityKgM3);
	const materialRemovalPct = safeDivide(massRemoved, startingMass);
	const buyToFly = safeDivide(startingMass, inputs.finishedMassKg);

	// Roughing — one figure per stage
	const roughingStages: BilletRoughingStageResult[] = inputs.roughingStages.map((s) => {
		const stageVol = removalVol * s.share;
		const cut = roughingCutHours(stageVol, s.mrrCm3Min, s.factor);
		return {
			cutHrs: cut,
			effectiveMrrCm3Min: s.mrrCm3Min * s.factor,
			fixedHrs: s.fixedHrs,
			removalVolumeCm3: stageVol,
			share: s.share,
			totalHrs: round1(cut + s.fixedHrs),
		};
	});
	const roughingTotal = round1(roughingStages.reduce((acc, s) => acc + s.totalHrs, 0));

	// Finishing
	const toolpathLength = finishToolpathLengthM(inputs.finishAreaM2, inputs.stepoverMm, inputs.finishPasses);
	const effectiveFeed = inputs.finishFeedMmMin * inputs.finishingFactor;
	const finishCut = finishingCutHours(toolpathLength, inputs.finishFeedMmMin, inputs.finishingFactor);
	const finishTotal = round1(finishCut + inputs.finishingFixedHrs);

	// Planning outputs
	const baseCnc = round1(roughingTotal + finishTotal + inputs.otherCncHrs);
	const contingency = round1(baseCnc * inputs.contingencyPct);
	const planned = round1(baseCnc + contingency);
	const shifts = round1(safeDivide(planned, inputs.hoursPerShift));
	const days = round1(safeDivide(planned, inputs.hoursPerShift * inputs.shiftsPerDay));

	const breakdown: BilletPlanStageBreakdown[] = [];
	roughingStages.forEach((s, i) => {
		breakdown.push({ hours: s.totalHrs, pctOfPlanned: safeDivide(s.totalHrs, planned), stage: roughingStages.length > 1 ? `Roughing ${i + 1}` : "Roughing" });
	});
	breakdown.push({ hours: finishTotal, pctOfPlanned: safeDivide(finishTotal, planned), stage: "Finishing" });
	breakdown.push({ hours: inputs.otherCncHrs, pctOfPlanned: safeDivide(inputs.otherCncHrs, planned), stage: "Other CNC / inspection" });
	breakdown.push({ hours: contingency, pctOfPlanned: safeDivide(contingency, planned), stage: "Schedule contingency" });

	return {
		baseCncHrs: baseCnc,
		breakdown,
		buyToFlyRatio: buyToFly,
		contingencyHrs: contingency,
		effectiveFinishFeedMmMin: effectiveFeed,
		equivalentShifts: shifts,
		equivalentWorkingDays: days,
		finishToolpathLengthM: toolpathLength,
		finishingCutHrs: finishCut,
		finishingTotalHrs: finishTotal,
		massRemovedKg: massRemoved,
		materialRemovalPct,
		plannedOccupancyHrs: planned,
		rectangularMassKg: rectMass,
		removalVolumeCm3: removalVol,
		roughingStages,
		roughingTotalHrs: roughingTotal,
		startingMassKg: startingMass,
	};
}
